import type { Locale } from "./locale";
import { enMessages, type UiKey } from "./enMessages";
import { esMessages } from "./esMessages";

export type { UiKey };

/** Flat UI strings (components). */
export const ui = {
  uk: {
    "meta.description":
      "DMeterLine — безкоштовний калькулятор витрат AWS та LLM: оціни місячні витрати на хмару (Lambda, API Gateway, DynamoDB, RDS, токени) до релізу. Орієнтир для продуктових команд і стартапів.",
    "meta.keywords":
      "калькулятор витрат AWS, оцінка витрат хмари, вартість AWS, serverless вартість, Lambda DynamoDB RDS ціна, вартість LLM, OpenAI GPT вартість, ціна токенів LLM, бюджет інфраструктури, витрати до релізу, хостинг SaaS, FinOps, DMeterLine",
    "lang.switch.uk": "УК",
    "lang.switch.en": "EN",
    "lang.switch.es": "ES",
    "lang.aria": "Мова інтерфейсу",

    "hero.eyebrow": "Before you build → know how much it will cost",
    "hero.title": "DMeterLine",
    "hero.lede.before": "Опиши фічу текстом. Парсинг → структура. ",
    "hero.lede.strong": "Формули",
    "hero.lede.after": " рахують AWS + LLM, не «магія» моделі.",
    "hero.disclaimer":
      "Це орієнтир для планування, а не фактичний біллінг AWS чи інших провайдерів.",

    "accuracy.title": "Наскільки точні цифри?",
    "accuracy.lead":
      "Це для порівняння варіантів і порядку величини — не прогноз фактурного рахунку AWS чи постачальника LLM.",
    "accuracy.pt1":
      "Модель спрощує архітектуру (час Lambda, патерни DynamoDB, змішана ціна токенів тощо) і не бачить ваш реальний трафік.",
    "accuracy.pt2":
      "Тарифи — публічні орієнтири: без ваших знижок, Reserved, Free Tier і деталізації як у Cost Explorer.",
    "accuracy.pt3":
      "Регіон і обраний стек задаються множниками й типовими конфігураціями, а не точною копією вашого акаунту.",
    "accuracy.pt4":
      "Поле «фактичний чек» допомагає звірити очікування моделі з реальністю.",
    "accuracy.bandsSimple":
      "У режимі «Простий» смуга під сумою навмисно широка (~0,48×–1,92× від точкової оцінки), щоб показати невизначеність.",
    "accuracy.bandsTechnical":
      "У «Технічному» смуга вужча (~0,86×–1,14×) навколо тієї ж моделі — усе одно орієнтир, не квота й не SLA.",

    "mode.planning": "Планування",
    "mode.description": "Опис",
    "mode.aria": "Спосіб введення навантаження",

    "planning.title": "Параметри навантаження",
    "planning.hint":
      "Прямі цифри й фічі → той самий workload, що після парсингу. Рахунок — формули.",
    "planning.usersDaily": "Користувачів на день (DAU)",
    "planning.requestsPerUser": "Дій / API на користувача на день",
    "planning.features": "Фічі архітектури",
    "planning.feat.chat": "Чат / повідомлення",
    "planning.feat.storage": "Зберігання даних",
    "planning.feat.search": "Пошук / історія",
    "planning.feat.ai": "AI / LLM у запитах",
    "planning.feat.realtime": "Realtime / WebSocket",
    "planning.feat.files": "Файли / об'єктне сховище",
    "planning.aiSection": "Параметри LLM",
    "planning.promptTokens": "Середньо токенів prompt",
    "planning.completionTokens": "Середньо токенів completion",
    "planning.aiShare": "Частка запитів, що йдуть у LLM",

    sampleInput: `Chat app:
- 10k users daily
- 100 messages per user
- messages stored
- search history
- AI responses via GPT`,

    "form.title": "Опис фічі",
    "form.hint":
      "MVP: евристичний парсер (без API). Далі той самий JSON з LLM у Lambda.",
    "form.submit": "Розпарсити → workload",
    "form.tryPlanningTemplates": "Швидкі шаблони й слайдери",
    "form.tryPlanningHint":
      "Без набору тексту — POC, MVP, чат і інше у вкладці «Планування».",
    "form.jsonLabel": "Structured workload (rules input)",

    "breakdown.title": "💰 Cost breakdown",
    "breakdown.total": "Разом (орієнтир)",
    "breakdown.perMonth": "/ міс",

    "whatif.title": "What-if",
    "whatif.subPlanning":
      "Множники до базового workload з форми (не змінюють поля планування).",
    "whatif.subDescription":
      "Множники до workload після парсингу тексту (текст у полі не змінюють).",
    "whatif.users": "Користувачі / день:",
    "whatif.requests": "Дій / користувач:",
    "whatif.liveTotal": "Поточний разом",

    "scaling.title": "📈 Scaling (базовий workload × користувачі)",
    "scaling.sub": "Крива від базового workload; осі — користувачі / день та $/міс.",
    "scaling.chartAria": "Графік вартості при масштабуванні",
    "scaling.barTitle": "{{users}} users → {{usd}} USD/mo",
    "scaling.legend": "Зараз: ~{{dau}} DAU → підсвічено найближчий стовпчик",

    "cp.title": "Cost vs performance",
    "cp.cheaper": "Дешевше",
    "cp.balanced": "Баланс",
    "cp.faster": "Швидше",

    "rec.risks": "⚠️ Ризики",
    "rec.risksEmpty": "Немає критичних прапорців за поточними правилами.",
    "rec.suggestions": "🔧 Оптимізації",
    "rec.effort": "зусилля:",
    "rec.arch": "🏗️ Architecture hints",

    "footer.note": "Орієнтовні оцінки — звіряй з реальними біллами AWS/OpenAI.",
    "footer.legal":
      "DMeterLine показує лише індикативний прогноз витрат за спрощеними припущеннями та публічними орієнтирами цін. Це не офіційний розрахунок, комерційна пропозиція, гарантія витрат і не податкова, бухгалтерська чи юридична консультація. Реальні суми залежать від тарифів провайдерів, регіону, знижок, безкоштовних лімітів, фактичного навантаження та змін умов. Оператор сервісу не несе відповідальності за рішення, договори чи збитки, прийняті лише на основі цього інструменту.",
    "export.legalHeading": "Відмова від відповідальності",
    "footer.contactLead": "Ліцензії, корпоративний доступ, купівля — ",
    "footer.contactAria": "Написати електронною поштою",

    "title.suffix": " — before you build",

    "error.title": "Щось пішло не так",
    "error.body":
      "Спробуй оновити сторінку. Якщо повторюється — перевір консоль або надішли цей текст підтримці.",
    "error.retry": "Оновити",

    "effort.low": "низьке",
    "effort.medium": "середнє",
    "effort.high": "високе",

    "toolbar.region": "Регіон AWS (множник)",
    "toolbar.regionHelp":
      "Грубий коефіцієнт до орієнтиру us-east-1; усі суми в breakdown масштабуються.",
    "toolbar.budgetCap": "Бюджет / міс (USD, опційно)",
    "toolbar.budgetPlaceholder": "напр. 500",
    "toolbar.exportTitle": "Звіт",
    "share.title": "Сценарій",
    "share.hint": "Посилання або JSON-файл — без доступу до AWS.",
    "share.copyLink": "Копіювати посилання",
    "share.linkCopied": "Посилання скопійовано",
    "share.linkTooLong": "Занадто великий для посилання — завантаж JSON.",
    "share.exportJson": "Завантажити JSON",
    "share.importJson": "Імпорт JSON",
    "share.importAria": "Імпорт сценарію з JSON-файлу",
    "share.importOk": "Сценарій завантажено.",
    "share.importInvalid": "Некоректний JSON або непідтримувана версія.",
    "region.af-south-1": "af-south-1 — Кейптаун",
    "region.ap-east-1": "ap-east-1 — Гонконг",
    "region.ap-northeast-1": "ap-northeast-1 — Токіо",
    "region.ap-northeast-2": "ap-northeast-2 — Сеул",
    "region.ap-northeast-3": "ap-northeast-3 — Осака",
    "region.ap-south-1": "ap-south-1 — Мумбаї",
    "region.ap-south-2": "ap-south-2 — Хайderabad",
    "region.ap-southeast-1": "ap-southeast-1 — Сінгапур",
    "region.ap-southeast-2": "ap-southeast-2 — Сідней",
    "region.ap-southeast-3": "ap-southeast-3 — Джакарта",
    "region.ap-southeast-4": "ap-southeast-4 — Мельбурн",
    "region.ca-central-1": "ca-central-1 — Монреаль",
    "region.eu-central-1": "eu-central-1 — Франкфурт",
    "region.eu-central-2": "eu-central-2 — Цюрих",
    "region.eu-north-1": "eu-north-1 — Стокгольм",
    "region.eu-south-1": "eu-south-1 — Мілан",
    "region.eu-south-2": "eu-south-2 — Іспанія",
    "region.eu-west-1": "eu-west-1 — Ірландія",
    "region.eu-west-2": "eu-west-2 — Лондон",
    "region.eu-west-3": "eu-west-3 — Париж",
    "region.il-central-1": "il-central-1 — Тель-Авів",
    "region.me-central-1": "me-central-1 — ОАЕ",
    "region.me-south-1": "me-south-1 — Бахрейн",
    "region.sa-east-1": "sa-east-1 — Сан-Паулу",
    "region.us-east-1": "us-east-1 — N. Virginia (США)",
    "region.us-east-2": "us-east-2 — Огайо",
    "region.us-west-1": "us-west-1 — N. California",
    "region.us-west-2": "us-west-2 — Орегон",

    "budget.over":
      "Орієнтир {{total}} перевищує встановлений бюджет {{cap}} / міс. Перевір what-if та оптимізації.",

    "export.copy": "Копіювати Markdown",
    "export.download": "Завантажити .md",
    "export.downloadHtml": "Завантажити HTML (графіки)",
    "export.printHint":
      "Відкрий файл у браузері. Для PDF: меню Друк → Зберегти як PDF.",
    "export.colItem": "Стаття",
    "export.colPct": "Частка",
    "export.colUsd": "USD / міс",
    "export.workloadSection": "Workload (JSON)",
    "export.assumptionsHeading": "Припущення моделі",
    "export.copied": "Скопійовано",
    "export.failed": "Не вдалося скопіювати",
    "export.downloadPdf": "Завантажити PDF",
    "export.pdfBusy": "Готуємо PDF…",
    "export.pdfError": "Не вдалося зібрати PDF. Спробуй HTML → Друк у браузері.",
    "export.appendix.trustTitle": "Довіра, версії та аудит",
    "export.appendix.modelVersion": "Версія моделі",
    "export.appendix.schemaVersion": "Версія схеми сценарію",
    "export.appendix.coefficientsDate": "Дата оновлення коефіцієнтів",
    "export.appendix.referencesTitle": "Референси цін (припущення)",
    "export.appendix.snapshotTitle": "Знімок оцінки (важелі на момент експорту)",
    "export.appendix.region": "Регіон AWS",
    "export.appendix.audience": "Режим перегляду / аудиторія",
    "export.appendix.llmProfile": "Профіль цін LLM",
    "export.appendix.computeProfile": "Профіль обчислень",
    "export.appendix.displayCurrency": "Валюта відображення в UI",
    "export.appendix.budgetCap": "Кап бюджету",
    "export.appendix.budgetNone": "Немає",
    "export.appendix.savingsOn": "Увімкнені важелі економії",
    "export.appendix.savingsNone": "Жоден не увімкнено",
    "export.appendix.stackTitle": "Стек розгортання",
    "export.appendix.notesTitle": "Нотатки до сценарію",
    "export.appendix.disclaimer":
      "Суми в звіті в USD за публічними орієнтирами та евристиками; EUR (якщо показано) — лише індикативний курс.",
    "export.readonlyBanner":
      "Знімок лише для перегляду — повне редагування в основному застосунку. Цифри індикативні, не рахунок.",
    "export.copyNotion": "Копіювати Markdown для Notion",
    "export.downloadReadonlyHtml": "Завантажити read-only HTML",
    "export.downloadPackMd": "Пакет сценаріїв (.md)",
    "export.downloadPackJson": "Пакет сценаріїв (.json)",
    "export.notionCopied": "Markdown для Notion скопійовано",

    "trust.panelTitle": "Чому цим цифрам можна довіряти?",
    "trust.panelLead":
      "Версіонуємо модель і схему, фіксуємо дату зміни коефіцієнтів і даємо посилання на публічні джерела цін. У експорті — аудит регіону, стеку й перемикачів.",
    "trust.coefficientsLabel": "Коефіцієнти оновлено",
    "trust.link.calculator": "AWS Pricing Calculator",
    "trust.link.pricing": "Публічні ціни AWS",
    "trust.link.docs": "Огляд AWS Billing",

    "enterprise.title": "Enterprise (незабаром)",
    "enterprise.lead":
      "Короткі стабільні посилання (бекенд), Slack / webhooks, синхрон нотаток, CSV Cost Explorer і глибший SSO — у дорожній карті.",
    "enterprise.b1": "Стабільні short links і робочі простори",
    "enterprise.b2": "Webhook / Slack з підсумком сценарію",
    "enterprise.b3": "Notion / Confluence через API",
    "enterprise.b4": "Коментарі до сценарію з синком для команди",
    "enterprise.b5": "Імпорт CSV / CUR для actual vs model",

    "share.previewOpen": "Відкрити read-only preview",
    "share.scenarioNotesLabel": "Нотатки (локально + у JSON)",
    "share.scenarioNotesPlaceholder":
      "Припущення, контекст для стейкхолдерів, посилання…",

    "preview.title": "Перегляд сценарію",
    "preview.subtitle":
      "Орієнтовні суми з hash-посилання — у цей браузер нічого не записується, доки не відкриєш повний редактор.",
    "preview.invalid":
      "Немає або некоректне посилання. Скопіюй лінк у головному застосунку або імпортуй JSON.",
    "preview.openFull": "Відкрити повний редактор",

    "toolbar.displayCurrency": "Валюта відображення",
    "toolbar.currencyUsd": "USD (модель)",
    "toolbar.currencyEur": "EUR (індикативно)",
    "toolbar.currencyEurHint":
      "~0,92 EUR за 1 USD лише для відображення; модель лишається в USD.",

    "csv.importButton": "Імпорт CSV Cost Explorer (сума)",
    "csv.importHint":
      "Груба сума з AWS CSV — заповнює «фактичний чек»; якщо у файлі є колонка сервісу, нижче з’явиться порівняння по категоріях.",
    "csv.importOk": "Сума з CSV розпізнана.",
    "csv.importBad": "Не вдалося розпарсити суму з CSV.",
    "csv.compare.title": "Факт vs модель (за категоріями)",
    "csv.compare.hint":
      "Назви сервісів з CSV зіставляються з рядками моделі евристично — це не точне відтворення Cost Explorer.",
    "csv.compare.colLine": "Рядок",
    "csv.compare.colModel": "Модель / міс",
    "csv.compare.colCsv": "CSV / міс",
    "csv.compare.colDelta": "Δ до моделі",
    "csv.compare.unmapped": "Інше (не зіставлені сервіси)",
    "csv.compare.clear": "Скинути розбивку CSV",

    "namedScenarios.packHint":
      "Експорт усіх збережених знімків і поточного сценарію однією таблицею Markdown або JSON.",
    "namedScenarios.packColScenario": "Сценарій",
    "namedScenarios.packColTotal": "Разом / міс (USD)",
    "namedScenarios.currentLabel": "Поточний (редактор)",

    "nav.footerAria": "Сайт",
    "nav.home": "Калькулятор",
    "nav.howItWorks": "Як це працює",
    "nav.privacy": "Конфіденційність",
    "nav.terms": "Умови",
    "nav.sidebarAria": "Головна навігація",
    "nav.menuOpen": "Відкрити меню",
    "nav.menuClose": "Закрити меню",
    "nav.collapseSidebar": "Сховати меню",
    "nav.expandSidebar": "Показати меню",
    "nav.sidebarTagline": "Орієнтир AWS і LLM — до релізу.",
    "nav.sidebarStripAria": "Коротко про інструмент",
    "nav.sidebarPill1": "На формулах",
    "nav.sidebarPill2": "AWS + LLM",
    "nav.sidebarPill3": "Експорт і шаринг",
    "nav.viewModeHint":
      "Режим Simple / Technical: перемикач у лівому меню (блок VIEW). Якщо меню сховане — відкрий панель зліва.",
    "scrollTop.aria": "Нагору",

    "doc.how.title": "Як працює DMeterLine",
    "doc.how.body": `DMeterLine перетворює короткий опис фічі або числа планування на структуроване навантаження (користувачі, запити, можливості, опційно LLM), далі застосовує спрощені орієнтири публічних цін у стилі AWS та токенів LLM.

Результат лише індикативний: це не рахунок AWS, не комерційна пропозиція й не юридична чи податкова консультація. Регіони дають грубий множник відносно us-east-1; вибір стеку додає або прибирає основні статті витрат (API Gateway, Lambda, DynamoDB, RDS, CloudFront тощо).

Секція «Порівняти з фактичним чеком» показує відсоткову різницю з моделлю. Експортуй звіти або ділися JSON — обліковий запис у хмарі не потрібен.

Формули й тексти вдосконалюються; перед рішеннями звіряй суми з реальними біллами провайдерів.`,

    "doc.privacy.title": "Конфіденційність",
    "doc.privacy.body": `DMeterLine працює як клієнтський вебзастосунок. Дані сценарію зберігаються в браузері (localStorage), щоб запам’ятати введення. У цій версії немає логіну чи бази користувачів.

Якщо в конфігурації сайту ввімкнено опційну аналітику (наприклад Plausible), постачальник може обробляти базову статистику згідно своєї політики — зазвичай без файлів cookie.

Не вставляй у калькулятор секрети, облікові дані чи персональні дані. Посилання та JSON-кодування містять твій сценарій — стався до них як до чутливих матеріалів.

Питання щодо приватності — через email у підвалі сторінки.`,

    "doc.terms.title": "Умови використання",
    "doc.terms.body": `Користуючись DMeterLine, ти погоджуєшся, що всі суми — оцінки на спрощених припущеннях. Оператор не гарантує точність, придатність для конкретної мети чи збіг з майбутніми біллами.

Інструмент не замінює професійні консультації з архітектури, безпеки, комплаєнсу, податків, бухгалтерії чи права. Відповідальність за рішення на основі оцінок лежить на тобі.

Сервіс надається «як є» без гарантій у межах, дозволених законом. Відповідальність обмежується в максимальних межах для безкоштовного планувального інструменту такого типу.

Умови можуть оновлюватися; подальше використання означає прийняття змін.`,

    "meta.ogTitleSuffix": "орієнтовне планування витрат AWS та LLM",

    "breakdown.linesMonthlyHint":
      "Рядки нижче завжди в USD/міс; лише підсумковий total змінюється за обраним режимом.",
    "breakdown.showTotalAs": "Показувати підсумок як",
    "breakdown.unit.month": "/ місяць",
    "breakdown.unit.year": "/ рік",
    "breakdown.unit.perDau": "/ DAU / міс",
    "breakdown.unit.perMillionReq": "/ 1M запитів",
    "breakdown.unitSuffix.month": "/ міс (як рядки)",
    "breakdown.unitSuffix.year": "/ рік (×12)",
    "breakdown.unitSuffix.perDau": "/ активний користувач / міс",
    "breakdown.unitSuffix.perMillionReq": "/ 1M запитів / міс",

    "llmProfile.title": "Профіль цін LLM",
    "llmProfile.hint":
      "Грубий рівень відносно базових ставок токенів (не live-котирування). Сумується з важелем «дешевший LLM».",
    "llmProfile.economy": "Економ",
    "llmProfile.standard": "Стандарт",
    "llmProfile.premium": "Преміум",

    "computeProfile.title": "Профіль обчислень навантаження",
    "computeProfile.hint":
      "CPU: базовий проксі Lambda/egress. GPU-стиль: вищі множники (наближено — не ціна виділених GPU-інстансів).",
    "computeProfile.cpu": "CPU / API",
    "computeProfile.gpu": "GPU-стиль",

    "regionCompare.title": "Той самий сценарій — усі регіони",
    "regionCompare.hint":
      "Орієнтовні суми з поточним навантаженням, стеком, важелями, профілем LLM і профілем обчислень. Обраний регіон підсвічено.",
    "regionCompare.colRegion": "Регіон",
    "regionCompare.colTotal": "Разом / міс",
    "regionCompare.colDelta": "до обраного",
    "regionCompare.deltaFoot":
      "Δ% — сума в цьому регіоні відносно зараз обраного (те саме навантаження й налаштування моделі).",

    "actualBill.title": "Порівняти з фактичним чеком",
    "actualBill.hint":
      "Необов’язково: введи реальну суму за місяць (USD), щоб побачити % різниці з цією моделлю (орієнтир).",
    "actualBill.label": "Фактично за місяць (USD)",
    "actualBill.placeholder": "напр. 842",
    "actualBill.invalid": "Введи додатне число.",
    "actualBill.vsModel":
      "Факт {{actual}} vs модель {{model}} — {{pct}}% до моделі (плюс = заплатив більше за оцінку).",

    "namedScenarios.title": "Збережені сценарії",
    "namedScenarios.hint":
      "До 5 іменованих знімків у цьому браузері (ті самі дані, що в share/JSON). «Завантажити» замінює поточну форму.",
    "namedScenarios.namePlaceholder": "Назва (напр. Prod Q2)",
    "namedScenarios.save": "Зберегти поточний",
    "namedScenarios.load": "Завантажити",
    "namedScenarios.delete": "Прибрати",
    "namedScenarios.empty": "Ще немає збережених сценаріїв.",
    "namedScenarios.max": "Максимум 5: новий зверху, найстаріший зникає.",

    "savings.title": "Міри економії (орієнтир)",
    "savings.hint":
      "Увімкни, якщо плануєш Spot, committed Dynamo, дешевший LLM або вимкнення на вихідних — суми й графіки перерахуються.",
    "savings.spot": "Spot / preemptible compute",
    "savings.spotHelp":
      "Знижка ~58% лише на GB·s у рядку Lambda (виклики без змін). Для Batch, Fargate Spot тощо.",
    "savings.dynamo": "Committed / provisioned DynamoDB",
    "savings.dynamoHelp": "~22% знижка на рядок Dynamo при стабільному навантаженні.",
    "savings.llm": "Дешевший LLM-шар",
    "savings.llmHelp": "~28% знижка на LLM: batch, менша модель, кеш відповідей.",
    "savings.llmBatch": "LLM batch / async API (не realtime)",
    "savings.llmBatchHelp":
      "Ще ~32% знижка на рядок LLM після economy — async batch-ціноутворення проти інтерактивного API.",
    "savings.weekends": "Вимкнення на вихідних (dev / staging)",
    "savings.weekendsHelp":
      "Години EC2, EKS worker і RDS-інстансу в моделі ×5/7. Lambda, сховище, DNS, GuardDuty без змін. Не для 24/7 продакшену.",

    "stack.title": "Стек деплою (орієнтир)",
    "stack.hint":
      "Фронтенд, бекенд і БД змінюють breakdown. Нижче — Route 53, ALB+WAF, GuardDuty за потреби. Ціни — спрощено us-east-1 × регіон.",
    "stack.frontend": "Фронтенд / edge",
    "stack.frontend.apigw": "Лише API Gateway (типовий serverless)",
    "stack.frontend.cloudfront": "CloudFront (CDN)",
    "stack.frontend.ec2_spa": "SPA на EC2 (nginx)",
    "stack.backend": "Бекенд / compute",
    "stack.backend.lambda": "AWS Lambda",
    "stack.backend.ec2": "EC2 (ASG орієнтир)",
    "stack.backend.eks": "Amazon EKS",
    "stack.database": "Основна БД у моделі",
    "stack.database.dynamo": "Лише DynamoDB",
    "stack.database.rds": "RDS (основна)",
    "stack.database.both": "DynamoDB + RDS (поділ)",
    "stack.ec2AutoCount": "Кількість EC2 від DAU (авто)",
    "stack.ec2Class": "Тип інстансу бекенду",
    "stack.ec2Count": "Кількість інстансів (вручну)",
    "stack.rdsAutoSize": "Клас RDS від DAU (авто)",
    "stack.rdsClass": "Клас RDS (вручну)",
    "stack.rdsStorage": "Сховище RDS (GB, gp3)",
    "stack.rdsMultiAz": "Multi-AZ RDS у формулі",
    "stack.network": "Доступ до сервісу",
    "stack.network.public": "Публічний (інтернет-facing API)",
    "stack.network.private": "Приватний (VPN / DC / корп. мережа)",
    "stack.networkHelp":
      "Приватний — без публічного бекенду; більше NAT/VPC endpoints у breakdown і окремий текст плану про витік ендпоінтів.",
    "stack.extrasTitle": "Додатково (за потреби)",
    "stack.route53": "Route 53 — домен і DNS",
    "stack.route53Help":
      "Hosted zone + запити. Публічні сертифікати ACM для ALB/CloudFront/API GW зазвичай безкоштовні.",
    "stack.albWaf": "ALB + AWS WAF",
    "stack.albWafHelp":
      "Орієнтир L7 балансера, WAF ACL і запитів. Managed rule groups — зверху до моделі.",
    "stack.guardDuty": "Amazon GuardDuty",
    "stack.guardDutyHelp":
      "Загрози по VPC/DNS/CloudTrail — спрощений рядок; multi-account деталі — окремо.",

    "load.title": "Орієнтовне навантаження",
    "load.hintSimple":
      "Приблизна активність простими словами — для планування, не гарантія швидкодії.",
    "load.hint": "З workload: запити, RPS, частка AI — для масштабу, не для SLA.",
    "bottleneck.title": "Можливі вузькі місця",

    "preset.title": "Швидкі пресети",
    "preset.poc": "POC / спайк",
    "preset.mvp": "Ранній MVP",
    "preset.web_app": "Звичайний веб-додаток",
    "preset.mobile_backend": "API під мобільний застосунок",
    "preset.internal_tool": "Внутрішній / адмін-інструмент",
    "preset.api_light": "Легкий API",
    "preset.chat_app": "Чат-додаток",
    "preset.rag_search": "RAG / пошук",
    "preset.high_traffic_ai": "Високе навантаження + AI",

    "sensitivity.title": "Чутливість (+10%)",
    "sensitivity.dau": "DAU +10%",
    "sensitivity.req": "Дій / користувач +10%",
    "sensitivity.aiShare": "Частка запитів у LLM +10%",
    "sensitivity.tokens": "Токени prompt/completion +10%",

    "checklist.title": "Чеклист перед запуском",
    "checklist.cache_responses": "Кешувати відповіді / idempotent API де можливо",
    "checklist.token_budget": "Ліміти токенів і таймаути для LLM",
    "checklist.billing_alarms": "Billing alarms / budgets у AWS",
    "checklist.load_test": "Навантажувальний тест на очікуваний пік",
    "checklist.cdn_static": "CDN для статики та edge-кеш",
    "checklist.async_writes": "Асинхронні записи / черги для піків",
    "checklist.review_reserved":
      "Переглянути Reserved / Savings Plans після стабілізації",

    "scenario.title": "Порівняння сценаріїв A / B",
    "scenario.saveA": "Зберегти як A",
    "scenario.saveB": "Зберегти як B",
    "scenario.clearA": "Очистити A",
    "scenario.clearB": "Очистити B",
    "scenario.total": "Разом / міс",
    "scenario.deltaAB": "Різниця (B − A)",
    "scenario.deltaVsCurrent": "vs поточний орієнтир",
    "scenario.empty": "Збережи два зрізи, щоб порівняти витрати.",
    "scenario.snapshotMeta":
      "Базовий workload × користувачі {{u}}×, дії {{r}}× → {{usd}} / міс",

    "plan.title": "План інфраструктури (орієнтир)",
    "plan.subtitle":
      "Що зазвичай лежить за цифрами: VPC, edge, compute і дані — узгоджено з обраним стеком деплою.",
    "plan.subtitleSimple":
      "Коротко простими словами: що за сценарій і куди зазвичай йдуть гроші. «Для фахівців» — повний план мережі, Lambda, баз і безпеки.",

    "audience.label": "Режим",
    "audience.aria": "Рівень деталізації аналізу",
    "audience.simple": "Простий",
    "audience.technical": "Для фахівців",
    "audience.hintSimple":
      "Ширший діапазон витрат і зрозумілі формулювання — для бюджету з керівництвом чи без технічного фону.",
    "audience.hintTechnical":
      "Вужчий коридор оцінки, навантаження в стилі RPS і технічні ризики та план інфраструктури.",
    "audience.hintSimpleSidebar": "Прості слова й ширший діапазон витрат.",
    "audience.hintTechnicalSidebar": "Вужчий коридор і технічні деталі.",

    "breakdown.rangeBandSimple":
      "Орієнтивний діапазон (широкий): {{low}} – {{high}} — реальні суми залежать від знижок, регіону та фактичного використання.",
    "breakdown.rangeBandTechnical":
      "Вужчий індикативний коридор: {{low}} – {{high}} — усе одно не комерційна пропозиція; діють припущення моделі.",
    "breakdown.lineRange":
      "Індикативний діапазон для цього рядка: {{low}} – {{high}} (той самий режим невизначеності, що й для total).",

    "glossary.summary": "Глосарій: DAU, GB·s, egress…",
    "glossary.termDau": "DAU",
    "glossary.defDau":
      "Daily Active Users — користувачі, які зайшли принаймні раз за типовий день. Модель масштабує запити й сховище від цього числа.",
    "glossary.termGb": "GB·s (гігабайт-секунди)",
    "glossary.defGb":
      "Одиниця тарифікації Lambda: пам’ять функції × час виконання. Більше пам’яті або довші обробники збільшують цей рядок навіть при тій самій кількості запитів.",
    "glossary.termEgress": "Data transfer (egress)",
    "glossary.defEgress":
      "Вихідний трафік з AWS в інтернет (або між регіонами). Відповіді API, завантаження та стрімінг можуть додати egress, який на малих біллах не виділений окремим постачальником.",

    "whatif.subAudienceSimple":
      "Множники масштабують сценарій; сума в блоці — орієнтир «посередині» широкого діапазону зверху.",
    "whatif.subAudienceTechnical":
      "Мультиплікатори застосовуються до базового workload; картки чутливості — +10% DAU або дій на ту саму модель.",

    "flow.section.model": "Параметри моделі",
    "flow.section.results": "Деталізація та структура",
    "flow.detailsAccuracy": "Про точність — деталі за бажанням",
    "flow.detailsModel": "За потреби: знижки, рівень AI, стек деплою",
    "flow.detailsExport": "Експорт і посилання для команди",
    "flow.detailsExpert": "Для технічних: регіони, масштаб, сценарії, чутливість",

    "livePulse.title": "Оцінка наживо",
    "livePulse.sub":
      "Суми й смужки миттєво реагують на повзунки, важелі економії, профіль LLM і стек розгортання.",
    "livePulse.sparkCaption": "Динаміка суми (сесія)",
    "livePulse.sparkAria": "Лінійний графік місячної оцінки за поточну сесію",
    "livePulse.topCaption": "Найбільші статті витрат",
    "livePulse.empty": "Заповни сценарій вище — тут з’явиться динаміка та тренд.",

    "mix.title": "Структура витрат (частки)",
    "mix.pieHint":
      "Кільце — частка місячної суми по кожній статті (ті самі кольори, що й смуга та breakdown). Наведи на сектор — USD і %.",
    "mix.pieAria": "Кільцевий графік місячних витрат по статтях",
    "mix.barAria": "Горизонтальна смуга структури витрат",

    "theme.aria": "Тема оформлення",
    "theme.light": "Світла",
    "theme.dark": "Темна",
  },
  en: enMessages,
  es: esMessages,
} as const satisfies Record<Locale, Record<UiKey, string>>;

export function uiString(locale: Locale, key: UiKey): string {
  return ui[locale][key] ?? ui.en[key] ?? key;
}

/** Replace {{name}} placeholders. */
export function uiFormat(
  locale: Locale,
  key: UiKey,
  vars: Record<string, string | number>,
): string {
  let s = uiString(locale, key);
  for (const [k, v] of Object.entries(vars)) {
    s = s.replaceAll(`{{${k}}}`, String(v));
  }
  return s;
}
